import { useTranslation } from 'react-i18next';

import Alert from '@/components/atoms/Alert';
import type { PenRef, ProviderConfigError } from '@/domain/providerConfig';
import { serverErrorView } from '@/routes/shared/ProviderConfig/form/serverErrors';
import { parseLocale } from '@/utils/localeFormatters';

import styles from './ProviderCard.module.scss';

export interface CardMessagesProps {
    /** The last failed save and test, by what failed; null for none. A catalog load says its
     *  own next to its button. */
    errors: Record<'save' | 'test', ProviderConfigError | null>;
    pens: PenRef[];
}

/** The card-wide messages, only ever as copy: a provider's own text is never shown. */
function CardMessages({ errors, pens }: CardMessagesProps) {
    const { t, i18n } = useTranslation();
    const list = new Intl.ListFormat(parseLocale(i18n.language), { type: 'conjunction' });
    const views = Object.entries(errors).flatMap(([source, error]) =>
        error === null ? [] : [{ source, ...serverErrorView(error) }]
    );

    return (
        // Always mounted: a live region added together with its text is not announced.
        <div className={styles.messages} aria-live="assertive">
            {views.map((view) => {
                if (view.formMessageKey === null) return null;
                const names = view.cageIds.map(
                    (cageId) => pens.find((pen) => pen.cageId === cageId)?.name ?? String(cageId)
                );
                // The wrapper is the live region, so each Alert adds none of its own.
                return (
                    <Alert key={view.source} tone="danger" size="small" live="off">
                        {t(view.formMessageKey)}
                        {names.length > 0 && (
                            <span className={styles.errorPens}>
                                {t('providerConfig.errors.pens', { pens: list.format(names) })}
                            </span>
                        )}
                    </Alert>
                );
            })}
        </div>
    );
}

export default CardMessages;
export { CardMessages };
